import {
  defineProduct,
  defineOffer,
  defineImage,
  definePerson,
  defineArticle,
} from 'nuxt-schema-org/schema';
import type { ProductResponse } from '~/types/ecommerce';
import type { Article, BlockImageNode, BlockNode } from '~/types/strapi';

type ItemAvailability
  = 'BackOrder' | 'Discontinued' | 'InStock' | 'InStoreOnly' | 'LimitedAvailability'
    | 'OnlineOnly' | 'OutOfStock' | 'PreOrder' | 'PreSale' | 'SoldOut';

type OfferItemCondition = 'NewCondition' | 'RefurbishedCondition' | 'UsedCondition';

const resolveItemAvailability = (product: ProductResponse): ItemAvailability => {
  let availability: ItemAvailability = product.isInStock ? 'InStock' : 'OutOfStock';

  if (product.isInStore) {
    availability = 'InStoreOnly';
  }

  if (product.orderType == 'Preorder') {
    availability = 'PreOrder';
  }

  if (product.orderType == 'Backorder') {
    availability = 'BackOrder';
  }

  if (product.vendor != 'KyGunCo') {
    availability = 'OnlineOnly';
  }

  return availability;
};

const resolveItemCondition = (product: ProductResponse): OfferItemCondition => {
  switch (product.condition) {
    case 'New':
      return 'NewCondition';
    case 'Blemished':
      return 'RefurbishedCondition';
    case 'Used':
      return 'UsedCondition';
    default:
      return 'NewCondition';
  }
};

// TODO: add shippingDetails to offers
// TODO: add hasMerchantReturnPolicy to offers
export const defineSchemaOrgProduct = (product: ProductResponse) => defineProduct({
  name: product.metaTitle ?? product.name,
  description: product.longDescription ?? product.metaDescription ?? product.name,
  sku: product.id,
  mpn: product.mpn,
  gtin: product.upc,
  brand: product.manufacturer
    ? { '@type': 'Brand', 'name': product.manufacturer.name }
    : undefined,
  image: [
    product.imageUrl as string,
    ...product.assets.map(a => a.url as string),
  ],
  offers: defineOffer({
    price: product.price,
    priceCurrency: 'USD',
    url: withSiteUrl(`/product/${product.slug}`).value,
    availability: resolveItemAvailability(product),
    itemCondition: resolveItemCondition(product),
  }),
  aggregateRating: product.reviewCount
    ? {
        worstRating: 1,
        bestRating: 5,
        ratingValue: product.averageScore,
        reviewCount: product.reviewCount,
        ratingCount: product.reviewCount,
      }
    : undefined,
});

export const defineSchemaOrgArticle = (article: Article) => {
  const images: {
    url: string;
    caption?: string;
    height?: number;
    width?: number;
  }[] = [];

  const collect = (node: BlockNode | BlockImageNode) => {
    if (node.type == 'image') {
      const imageNode = node as BlockImageNode;

      images.push(
        defineImage({
          url: imageNode.image.url,
          caption: imageNode.image.caption,
          height: imageNode.image.height,
          width: imageNode.image.width,
        }),
      );
    }

    node.children?.forEach(n => collect(n));
  };

  if (article.image?.data?.attributes?.url) {
    images.push(defineImage({ url: article.image.data.attributes.url }));
  }

  if (article.banner?.data?.attributes?.url) {
    images.push(defineImage({ url: article.banner.data.attributes.url }));
  }

  article.content?.forEach(n => collect(n));

  return defineArticle({
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    author: article.author ? definePerson({ name: article.author }) : undefined,
    image: images,
    thumbnailUrl: article.seo?.metaImage?.data?.attributes?.url
      ?? article.image?.data?.attributes?.url,
  });
};
